import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSources,
  patchSource,
  selectAll,
  validateAccess,
} from "../api/sources";
import type { Source } from "../types";

export function useSources(
  projectId: string | undefined,
  opts?: { selected?: boolean },
) {
  return useQuery<Source[]>({
    queryKey: ["sources", projectId, opts?.selected],
    queryFn: () => getSources(projectId!, opts),
    enabled: !!projectId,
    staleTime: 10_000,
  });
}

export function useToggleSource(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, selected }: { id: string; selected: boolean }) =>
      patchSource(id, { selected }),
    onMutate: async ({ id, selected }) => {
      await qc.cancelQueries({ queryKey: ["sources", projectId] });
      const snapshot = qc.getQueriesData<Source[]>({
        queryKey: ["sources", projectId],
      });

      qc.setQueriesData<Source[]>(
        { queryKey: ["sources", projectId] },
        (old) =>
          old?.map((source) =>
            source.id === id ? { ...source, selected } : source,
          ),
      );

      return { snapshot };
    },
    onSuccess: (updated) => {
      qc.setQueriesData<Source[]>(
        { queryKey: ["sources", projectId] },
        (old) =>
          old?.map((source) => (source.id === updated.id ? updated : source)),
      );
    },
    onError: (_error, _vars, context) => {
      context?.snapshot.forEach(([key, value]) => qc.setQueryData(key, value));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["sources", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
}

export function useSelectAll(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (selected: boolean) => selectAll(projectId, { selected }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
}

export function useValidateAccess(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => validateAccess(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources", projectId] });
    },
  });
}
